import { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { useTabs } from '../contexts/TabContext';
import { 
  FileText, Upload, CheckCircle2, XCircle, AlertCircle, Loader2,
  FileDown, Send, Edit, Eye, Plus, Trash2, RefreshCw, Sparkles,
  Building2, User, Calendar, CreditCard, FileCheck, Paperclip, Wand2
} from 'lucide-react';

const API = process.env.NODE_ENV === 'production' ? '' : (process.env.REACT_APP_BACKEND_URL || '');

// Kategorie-Farben
const KATEGORIE_FARBEN = {
  pflegegrad: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  grundsicherung: 'bg-blue-100 text-blue-700 border-blue-200',
  wohngeld: 'bg-purple-100 text-purple-700 border-purple-200',
  gericht: 'bg-amber-100 text-amber-700 border-amber-200',
  vbvg: 'bg-rose-100 text-rose-700 border-rose-200',
  sonstiges: 'bg-slate-100 text-slate-700 border-slate-200',
};

// Status-Farben
const STATUS_FARBEN = {
  hochgeladen: 'bg-blue-100 text-blue-700',
  wird_analysiert: 'bg-amber-100 text-amber-700',
  analysiert: 'bg-emerald-100 text-emerald-700',
  versandpaket_erstellt: 'bg-purple-100 text-purple-700',
  versendet: 'bg-green-100 text-green-700',
  fehler: 'bg-red-100 text-red-700',
};

export default function AntragsassistentPage() {
  // URL-Parameter für Klient-ID (z.B. von AkteBlick kommend)
  const { tabParams } = useTabs();
  const initialKlientId = tabParams?.klientId || '';
  
  // State
  const [vorlagen, setVorlagen] = useState([]);
  const [antraege, setAntraege] = useState([]);
  const [klienten, setKlienten] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('antraege');
  
  // Upload Dialog
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadKlientId, setUploadKlientId] = useState(initialKlientId);
  const [uploadVorlageId, setUploadVorlageId] = useState('');
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  
  // Antrag Detail Dialog
  const [selectedAntrag, setSelectedAntrag] = useState(null);
  const [antragDetailOpen, setAntragDetailOpen] = useState(false);
  const [generatingAnschreiben, setGeneratingAnschreiben] = useState(false);
  const [creatingVersandpaket, setCreatingVersandpaket] = useState(false);
  
  // PDF-Befüllung
  const [autoBefuellungLoading, setAutoBefuellungLoading] = useState(false);
  const [formularFelder, setFormularFelder] = useState([]);
  
  // Vorlage Dialog
  const [vorlageDialogOpen, setVorlageDialogOpen] = useState(false);
  const [editVorlage, setEditVorlage] = useState(null);
  
  // Wenn von AkteBlick kommend, Upload-Dialog direkt öffnen
  useEffect(() => {
    if (initialKlientId) {
      setUploadOpen(true);
    }
  }, [initialKlientId]);
  
  // Laden
  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [vorlagenRes, antraegeRes, klientenRes] = await Promise.all([
        fetch(`${API}/api/antragsassistent/vorlagen`),
        fetch(`${API}/api/antragsassistent/antraege`),
        fetch(`${API}/api/betreuung/klienten`),
      ]);
      
      if (vorlagenRes.ok) {
        const d = await vorlagenRes.json();
        setVorlagen(d.vorlagen || []);
      }
      if (antraegeRes.ok) {
        const d = await antraegeRes.json();
        setAntraege(d.antraege || []);
      }
      if (klientenRes.ok) {
        const d = await klientenRes.json();
        setKlienten(d || []);
      }
    } catch (e) {
      console.error('Laden fehlgeschlagen:', e);
      toast.error('Daten konnten nicht geladen werden');
    }
    setLoading(false);
  };

  // Upload Antrag
  const handleUpload = async () => {
    if (!uploadFile || !uploadKlientId) {
      toast.error('Bitte Datei und Klient auswählen');
      return;
    }
    
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', uploadFile);
      formData.append('klient_id', uploadKlientId);
      if (uploadVorlageId) formData.append('vorlage_id', uploadVorlageId);
      
      const res = await fetch(`${API}/api/antragsassistent/upload`, {
        method: 'POST',
        body: formData,
      });
      
      if (res.ok) {
        const data = await res.json();
        toast.success('Antrag wird analysiert...');
        setUploadOpen(false);
        setUploadFile(null);
        setUploadKlientId('');
        setUploadVorlageId('');
        
        // Polling für Analyse-Status
        pollAntragStatus(data.antrag_id);
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Upload fehlgeschlagen');
      }
    } catch (e) {
      toast.error('Upload fehlgeschlagen');
    }
    setUploading(false);
  };

  // Polling für Analyse-Status
  const pollAntragStatus = async (antragId) => {
    let attempts = 0;
    const maxAttempts = 30;
    
    const poll = async () => {
      try {
        const res = await fetch(`${API}/api/antragsassistent/antrag/${antragId}`);
        if (res.ok) {
          const antrag = await res.json();
          
          if (antrag.status === 'analysiert') {
            toast.success('Analyse abgeschlossen!');
            loadData();
            setSelectedAntrag(antrag);
            setAntragDetailOpen(true);
            return;
          } else if (antrag.status === 'fehler') {
            toast.error(`Analyse fehlgeschlagen: ${antrag.fehler || 'Unbekannter Fehler'}`);
            loadData();
            return;
          }
        }
        
        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(poll, 2000);
        } else {
          toast.warning('Analyse dauert länger als erwartet. Bitte später prüfen.');
          loadData();
        }
      } catch (e) {
        console.error('Polling error:', e);
      }
    };
    
    setTimeout(poll, 2000);
  };

  // Anschreiben generieren
  const handleGenerateAnschreiben = async () => {
    if (!selectedAntrag) return;
    
    setGeneratingAnschreiben(true);
    try {
      const res = await fetch(`${API}/api/antragsassistent/antrag/${selectedAntrag.id}/anschreiben`, {
        method: 'POST',
      });
      
      if (res.ok) {
        const data = await res.json();
        setSelectedAntrag(prev => ({ ...prev, anschreiben: data.anschreiben }));
        toast.success('Anschreiben erstellt');
      } else {
        toast.error('Anschreiben-Erstellung fehlgeschlagen');
      }
    } catch (e) {
      toast.error('Fehler beim Erstellen des Anschreibens');
    }
    setGeneratingAnschreiben(false);
  };

  // Versandpaket erstellen
  const handleCreateVersandpaket = async () => {
    if (!selectedAntrag) return;
    
    setCreatingVersandpaket(true);
    try {
      const res = await fetch(`${API}/api/antragsassistent/antrag/${selectedAntrag.id}/versandpaket`, {
        method: 'POST',
      });
      
      if (res.ok) {
        const data = await res.json();
        setSelectedAntrag(prev => ({ 
          ...prev, 
          status: 'versandpaket_erstellt',
          versandpaket_pfad: data.versandpaket_pfad 
        }));
        toast.success(`Versandpaket erstellt (${data.seiten} Seiten)`);
        loadData();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Versandpaket-Erstellung fehlgeschlagen');
      }
    } catch (e) {
      toast.error('Fehler beim Erstellen des Versandpakets');
    }
    setCreatingVersandpaket(false);
  };

  // Download Versandpaket
  const handleDownload = async (antragId) => {
    window.open(`${API}/api/antragsassistent/antrag/${antragId}/download`, '_blank');
  };

  // Download befülltes PDF
  const handleDownloadBefuellt = async (antragId) => {
    window.open(`${API}/api/antragsassistent/antrag/${antragId}/download-befuellt`, '_blank');
  };

  // Auto-Befüllung des PDFs
  const handleAutoBefuellung = async () => {
    if (!selectedAntrag) return;
    
    setAutoBefuellungLoading(true);
    try {
      const res = await fetch(`${API}/api/antragsassistent/antrag/${selectedAntrag.id}/auto-befuellen`, {
        method: 'POST',
      });
      
      if (res.ok) {
        const data = await res.json();
        if (data.success) {
          toast.success(`${data.befuellte_felder} Felder automatisch befüllt`);
          
          // Antrag-Details aktualisieren
          const antragRes = await fetch(`${API}/api/antragsassistent/antrag/${selectedAntrag.id}`);
          if (antragRes.ok) {
            const updatedAntrag = await antragRes.json();
            setSelectedAntrag(updatedAntrag);
          }
          
          // Liste aktualisieren
          loadData();
        } else {
          toast.warning(data.error || 'Keine Felder konnten automatisch befüllt werden');
          if (data.nicht_gematchte_felder?.length > 0) {
            console.log('Nicht gematchte Felder:', data.nicht_gematchte_felder);
          }
        }
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Auto-Befüllung fehlgeschlagen');
      }
    } catch (e) {
      toast.error('Fehler bei der automatischen Befüllung');
    }
    setAutoBefuellungLoading(false);
  };

  // Formularfelder lesen
  const loadFormularFelder = async (antragId) => {
    try {
      const res = await fetch(`${API}/api/antragsassistent/antrag/${antragId}/formularfelder`);
      if (res.ok) {
        const data = await res.json();
        setFormularFelder(data.felder || []);
        return data.ist_formular;
      }
    } catch (e) {
      console.error('Formularfelder laden fehlgeschlagen:', e);
    }
    return false;
  };

  // Klient-Name Helper
  const getKlientName = (klientId) => {
    const k = klienten.find(k => k.id === klientId);
    return k ? `${k.vorname} ${k.nachname}` : 'Unbekannt';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="antragsassistent-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
            <FileText className="w-6 h-6 text-blue-600" />
            Antragsassistent
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Intelligenter Assistent für Anträge mit automatischem Versandpaket
          </p>
        </div>
        
        <Dialog open={uploadOpen} onOpenChange={setUploadOpen}>
          <DialogTrigger asChild>
            <Button className="gap-2" data-testid="upload-antrag-btn">
              <Upload className="w-4 h-4" />
              Antrag hochladen
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle>Antragsformular hochladen</DialogTitle>
              <DialogDescription>
                Laden Sie ein PDF- oder DOCX-Formular hoch. Der Assistent analysiert es automatisch.
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              {/* Klient auswählen */}
              <div>
                <Label>Klient *</Label>
                <Select value={uploadKlientId} onValueChange={setUploadKlientId}>
                  <SelectTrigger data-testid="upload-klient-select">
                    <SelectValue placeholder="Klient auswählen..." />
                  </SelectTrigger>
                  <SelectContent>
                    {klienten.map(k => (
                      <SelectItem key={k.id} value={k.id}>
                        {k.vorname} {k.nachname}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {/* Vorlage auswählen (optional) */}
              <div>
                <Label>Vorlage (optional)</Label>
                <Select value={uploadVorlageId || "auto"} onValueChange={(v) => setUploadVorlageId(v === "auto" ? "" : v)}>
                  <SelectTrigger data-testid="upload-vorlage-select">
                    <SelectValue placeholder="Automatisch erkennen" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="auto">Automatisch erkennen</SelectItem>
                    {vorlagen.map(v => (
                      <SelectItem key={v.id} value={v.id}>
                        {v.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {/* Datei-Upload */}
              <div>
                <Label>Formular (PDF/DOCX)</Label>
                <div 
                  className="border-2 border-dashed rounded-lg p-6 text-center cursor-pointer hover:border-blue-400 transition-colors"
                  onClick={() => fileInputRef.current?.click()}
                >
                  {uploadFile ? (
                    <div className="flex items-center justify-center gap-2 text-emerald-600">
                      <FileCheck className="w-5 h-5" />
                      <span>{uploadFile.name}</span>
                    </div>
                  ) : (
                    <div className="text-slate-500">
                      <Upload className="w-8 h-8 mx-auto mb-2 text-slate-400" />
                      <p>Klicken zum Hochladen</p>
                      <p className="text-xs text-slate-400">PDF oder DOCX</p>
                    </div>
                  )}
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".pdf,.docx,.doc"
                  className="hidden"
                  onChange={(e) => setUploadFile(e.target.files?.[0])}
                  data-testid="upload-file-input"
                />
              </div>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setUploadOpen(false)}>
                Abbrechen
              </Button>
              <Button onClick={handleUpload} disabled={uploading || !uploadFile || !uploadKlientId}>
                {uploading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Wird analysiert...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-4 h-4 mr-2" />
                    Analysieren
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="antraege" className="gap-2">
            <FileText className="w-4 h-4" />
            Anträge ({antraege.length})
          </TabsTrigger>
          <TabsTrigger value="vorlagen" className="gap-2">
            <FileCheck className="w-4 h-4" />
            Vorlagen ({vorlagen.length})
          </TabsTrigger>
        </TabsList>

        {/* Anträge Tab */}
        <TabsContent value="antraege" className="mt-4">
          {antraege.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <FileText className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                <h3 className="text-lg font-medium text-slate-700">Keine Anträge</h3>
                <p className="text-sm text-slate-500 mt-1">
                  Laden Sie Ihr erstes Antragsformular hoch, um zu starten.
                </p>
                <Button className="mt-4" onClick={() => setUploadOpen(true)}>
                  <Upload className="w-4 h-4 mr-2" />
                  Antrag hochladen
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4">
              {antraege.map(antrag => (
                <Card 
                  key={antrag.id} 
                  className="hover:shadow-md transition-shadow cursor-pointer"
                  onClick={() => { setSelectedAntrag(antrag); setAntragDetailOpen(true); }}
                  data-testid={`antrag-card-${antrag.id}`}
                >
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                          <FileText className="w-5 h-5 text-blue-600" />
                        </div>
                        <div>
                          <h3 className="font-medium text-slate-800">
                            {antrag.erkannte_daten?.antragstyp || antrag.dateiname}
                          </h3>
                          <p className="text-sm text-slate-500">
                            {getKlientName(antrag.klient_id)} • {new Date(antrag.created_date).toLocaleDateString('de-DE')}
                          </p>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        <Badge className={STATUS_FARBEN[antrag.status] || 'bg-slate-100'}>
                          {antrag.status?.replace(/_/g, ' ')}
                        </Badge>
                        {antrag.status === 'versandpaket_erstellt' && (
                          <Button 
                            size="sm" 
                            variant="outline" 
                            onClick={(e) => { e.stopPropagation(); handleDownload(antrag.id); }}
                          >
                            <FileDown className="w-4 h-4 mr-1" />
                            PDF
                          </Button>
                        )}
                      </div>
                    </div>
                    
                    {/* Fortschritts-Indikatoren */}
                    {antrag.status === 'analysiert' && (
                      <div className="mt-3 flex items-center gap-4 text-xs">
                        <span className="flex items-center gap-1 text-emerald-600">
                          <CheckCircle2 className="w-3 h-3" />
                          {Object.values(antrag.felder || {}).filter(f => f.befuellt).length} Felder befüllt
                        </span>
                        {(antrag.felder_fehlen?.length || 0) > 0 && (
                          <span className="flex items-center gap-1 text-red-600">
                            <AlertCircle className="w-3 h-3" />
                            {antrag.felder_fehlen.length} fehlend
                          </span>
                        )}
                        <span className="flex items-center gap-1 text-blue-600">
                          <Paperclip className="w-3 h-3" />
                          {(antrag.anlagen || []).filter(a => a.vorhanden).length}/{(antrag.anlagen || []).length} Anlagen
                        </span>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Vorlagen Tab */}
        <TabsContent value="vorlagen" className="mt-4">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {vorlagen.map(vorlage => (
              <Card key={vorlage.id} className="hover:shadow-md transition-shadow">
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <Badge className={KATEGORIE_FARBEN[vorlage.kategorie] || KATEGORIE_FARBEN.sonstiges}>
                      {vorlage.kategorie}
                    </Badge>
                    {vorlage.ist_standard && (
                      <Badge variant="outline" className="text-xs">Standard</Badge>
                    )}
                  </div>
                  <CardTitle className="text-base mt-2">{vorlage.name}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-slate-500 line-clamp-2">
                    {vorlage.beschreibung}
                  </p>
                  <div className="mt-3 flex items-center gap-4 text-xs text-slate-500">
                    <span>{vorlage.benoetigte_felder?.length || 0} Felder</span>
                    <span>{vorlage.typische_anlagen?.length || 0} Anlagen</span>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>
      </Tabs>

      {/* Antrag Detail Dialog */}
      <Dialog open={antragDetailOpen} onOpenChange={setAntragDetailOpen}>
        <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto">
          {selectedAntrag && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <FileText className="w-5 h-5 text-blue-600" />
                  {selectedAntrag.erkannte_daten?.antragstyp || selectedAntrag.dateiname}
                </DialogTitle>
                <DialogDescription>
                  Klient: {getKlientName(selectedAntrag.klient_id)} • 
                  Status: <Badge className={STATUS_FARBEN[selectedAntrag.status]}>{selectedAntrag.status?.replace(/_/g, ' ')}</Badge>
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-6 py-4">
                {/* Erkannte Daten */}
                {selectedAntrag.erkannte_daten && (
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-medium text-slate-700">Erkannte Daten</h4>
                      {selectedAntrag.status === 'analysiert' && (
                        <Button 
                          size="sm" 
                          onClick={handleAutoBefuellung}
                          disabled={autoBefuellungLoading}
                          className="bg-purple-600 hover:bg-purple-700"
                          data-testid="btn-auto-befuellen"
                        >
                          {autoBefuellungLoading ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <>
                              <Wand2 className="w-4 h-4 mr-1" />
                              PDF auto-befüllen
                            </>
                          )}
                        </Button>
                      )}
                    </div>
                    <Card>
                      <CardContent className="p-4 space-y-2 text-sm">
                        <div className="flex items-center gap-2">
                          <strong>OCR-Methode:</strong>
                          {selectedAntrag.ocr_methode === 'claude_vision' ? (
                            <Badge className="bg-purple-100 text-purple-700">Claude Vision</Badge>
                          ) : (
                            <Badge className="bg-blue-100 text-blue-700">Standard OCR</Badge>
                          )}
                        </div>
                        <p><strong>Empfänger:</strong> {selectedAntrag.erkannte_daten.empfaenger?.name || 'Nicht erkannt'}</p>
                        <p><strong>Typ:</strong> {selectedAntrag.erkannte_daten.empfaenger?.typ || '-'}</p>
                        <p><strong>Zusammenfassung:</strong> {selectedAntrag.erkannte_daten.zusammenfassung}</p>
                        {selectedAntrag.befuellt_pdf_pfad && (
                          <div className="mt-2 pt-2 border-t">
                            <Badge className="bg-purple-100 text-purple-700 border-purple-200">
                              <CheckCircle2 className="w-3 h-3 mr-1" />
                              PDF befüllt
                            </Badge>
                            <Button 
                              size="sm" 
                              variant="link" 
                              className="text-purple-600 ml-2"
                              onClick={() => handleDownloadBefuellt(selectedAntrag.id)}
                            >
                              <FileDown className="w-3 h-3 mr-1" />
                              Herunterladen
                            </Button>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  </div>
                )}

                {/* Befüllte Felder */}
                <div>
                  <h4 className="font-medium text-slate-700 mb-2">Felder</h4>
                  <div className="grid gap-2">
                    {Object.entries(selectedAntrag.felder || {}).map(([name, feld]) => (
                      <div 
                        key={name} 
                        className={`flex items-center justify-between p-2 rounded border ${
                          feld.befuellt ? 'bg-emerald-50 border-emerald-200' : 'bg-amber-50 border-amber-200'
                        }`}
                      >
                        <span className="text-sm font-medium">{name}</span>
                        <div className="flex items-center gap-2">
                          {feld.befuellt ? (
                            <>
                              <span className="text-sm text-slate-600">{feld.wert}</span>
                              <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                            </>
                          ) : (
                            <>
                              <span className="text-xs text-amber-600">{feld.hinweis}</span>
                              <AlertCircle className="w-4 h-4 text-amber-500" />
                            </>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  {/* Fehlende Felder */}
                  {(selectedAntrag.felder_fehlen?.length || 0) > 0 && (
                    <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded">
                      <h5 className="text-sm font-medium text-red-700 mb-2">Fehlende Daten in Klientenakte:</h5>
                      <ul className="text-sm text-red-600 space-y-1">
                        {selectedAntrag.felder_fehlen.map((f, i) => (
                          <li key={i}>• {f.feld}: {f.hinweis}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>

                {/* Anlagen */}
                <div>
                  <h4 className="font-medium text-slate-700 mb-2">Anlagen</h4>
                  <div className="grid gap-2">
                    {(selectedAntrag.anlagen || []).map((anlage, i) => (
                      <div 
                        key={i}
                        className={`flex items-center justify-between p-2 rounded border ${
                          anlage.vorhanden ? 'bg-emerald-50 border-emerald-200' : 'bg-red-50 border-red-200'
                        }`}
                      >
                        <span className="text-sm">{anlage.name}</span>
                        <div className="flex items-center gap-2">
                          {anlage.vorhanden ? (
                            <>
                              <span className="text-xs text-emerald-600">In Akte</span>
                              <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                            </>
                          ) : (
                            <>
                              <span className="text-xs text-red-600">{anlage.hinweis}</span>
                              <XCircle className="w-4 h-4 text-red-500" />
                            </>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Anschreiben */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-medium text-slate-700">Anschreiben</h4>
                    <Button 
                      size="sm" 
                      variant="outline" 
                      onClick={handleGenerateAnschreiben}
                      disabled={generatingAnschreiben}
                    >
                      {generatingAnschreiben ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <>
                          <Sparkles className="w-4 h-4 mr-1" />
                          {selectedAntrag.anschreiben ? 'Neu generieren' : 'Generieren'}
                        </>
                      )}
                    </Button>
                  </div>
                  {selectedAntrag.anschreiben ? (
                    <Textarea 
                      value={selectedAntrag.anschreiben}
                      onChange={(e) => setSelectedAntrag(prev => ({ ...prev, anschreiben: e.target.value }))}
                      className="min-h-[200px] text-sm font-mono"
                    />
                  ) : (
                    <div className="p-4 bg-slate-50 border border-dashed rounded text-center text-slate-500">
                      Klicken Sie "Generieren", um ein Anschreiben zu erstellen
                    </div>
                  )}
                </div>
              </div>

              <DialogFooter className="gap-2">
                <Button variant="outline" onClick={() => setAntragDetailOpen(false)}>
                  Schließen
                </Button>
                
                {selectedAntrag.status === 'analysiert' && selectedAntrag.anschreiben && (
                  <Button onClick={handleCreateVersandpaket} disabled={creatingVersandpaket}>
                    {creatingVersandpaket ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <FileDown className="w-4 h-4 mr-2" />
                    )}
                    Versandpaket erstellen
                  </Button>
                )}
                
                {selectedAntrag.status === 'versandpaket_erstellt' && (
                  <>
                    <Button variant="outline" onClick={() => handleDownload(selectedAntrag.id)}>
                      <FileDown className="w-4 h-4 mr-2" />
                      PDF herunterladen
                    </Button>
                    <Button>
                      <Send className="w-4 h-4 mr-2" />
                      Versenden
                    </Button>
                  </>
                )}
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
